import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Loader2, HelpCircle, ArrowLeft, ArrowRight, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useLang, tx } from '../../i18n/LanguageContext';

export const NotesPanel = ({ courseId, lessonId, user, locked, onCount }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get(`/notes/${courseId}/${lessonId}`).then(({ data }) => { setNotes(data || []); onCount((data || []).length); }).catch(() => {});
  }, [courseId, lessonId, user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return (
    <div className="text-center py-8">
      <p className="text-slate-500 text-sm mb-4">{L('سجّل الدخول لكتابة ملاحظاتك الخاصة على هذا الدرس', 'Log in to write your private notes on this lesson')}</p>
      <Link to="/login" data-testid="notes-login-btn" className="inline-flex h-10 px-5 items-center rounded-xl bg-blue-600 text-white text-sm font-bold">{L('تسجيل الدخول', 'Log in')}</Link>
    </div>
  );
  if (locked) return <p data-testid="notes-locked" className="text-slate-500 text-sm text-center py-8">{L('اشترك لتتمكن من كتابة ملاحظات على هذا الدرس', 'Subscribe to write notes on this lesson')}</p>;
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/notes/${courseId}/${lessonId}`, { text });
      const next = [data, ...notes];
      setNotes(next); onCount(next.length); setText('');
    } catch (e) { toast.error(L('تعذر حفظ الملاحظة', 'Could not save the note')); }
    finally { setBusy(false); }
  };
  const remove = async (n) => {
    if (deleting) return;
    setDeleting(n.id);
    try {
      await api.delete(`/notes/${courseId}/${lessonId}/${n.id}`);
      const next = notes.filter(x => x.id !== n.id);
      setNotes(next); onCount(next.length);
    } catch (e) { toast.error(L('تعذر حذف الملاحظة', 'Could not delete the note')); }
    finally { setDeleting(null); }
  };
  return (
    <div data-testid="lesson-notes-panel">
      <p className="text-xs text-slate-500 mb-2">{L('ملاحظاتك خاصة بك ولا يراها غيرك', 'Your notes are private to you')}</p>
      <textarea data-testid="note-input" rows={3} value={text} onChange={e => setText(e.target.value)} placeholder={L('اكتب ملاحظة عن هذا الدرس...', 'Write a note about this lesson...')}
        className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100 transition-[border-color,box-shadow,background-color]" />
      <div className="flex justify-end mt-2 mb-5">
        <button type="button" data-testid="note-add-btn" disabled={!text.trim() || busy} onClick={add} className="h-9 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-[13px] font-semibold inline-flex items-center gap-2 transition-colors">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{L('حفظ الملاحظة', 'Save note')}</button>
      </div>
      <ul className="space-y-2">
        {notes.map(n => (
          <li key={n.id} data-testid={`note-${n.id}`} className="flex items-start gap-3 rounded-lg bg-amber-50/60 px-3 py-2.5">
            <StickyNote className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-800 whitespace-pre-line break-words">{n.text}</p>
              <span className="text-[11px] text-slate-400" dir="ltr">{n.created_at ? new Date(n.created_at).toLocaleString(lang === 'ar' ? 'ar' : 'en', { dateStyle: 'medium', timeStyle: 'short' }) : ''}</span>
            </div>
            <button type="button" data-testid={`note-delete-${n.id}`} onClick={() => remove(n)} disabled={deleting === n.id} className="text-slate-400 hover:text-rose-500 disabled:opacity-40"><Trash2 className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const QuizPanel = ({ quizId }) => {
  const { lang, isRTL } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [quiz, setQuiz] = useState(null);
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  useEffect(() => { if (quizId) api.get(`/quizzes/${quizId}`).then(({ data }) => setQuiz(data)).catch(() => setQuiz(null)); }, [quizId]);
  if (!quizId || !quiz) return <p data-testid="lesson-quiz-empty" className="text-slate-400 text-sm text-center py-8">{L('لا يوجد اختبار لهذا الدرس', 'No quiz for this lesson')}</p>;
  return (
    <div data-testid="lesson-quiz-panel" className="flex flex-wrap items-center gap-3">
      <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><HelpCircle className="w-5 h-5" /></span>
      <div className="flex-1 min-w-[160px]">
        <div className="text-sm font-bold text-slate-900">{lang === 'ar' ? quiz.titleAr : (quiz.titleEn || quiz.titleAr)}</div>
        <div className="text-xs text-slate-500 mt-0.5">{quiz.question_count} {L('سؤال', 'questions')}</div>
      </div>
      <Link to={`/quizzes/${quiz.id}`} data-testid="lesson-quiz-start" className="h-9 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold inline-flex items-center gap-1.5 transition-colors">{L('ابدأ الاختبار', 'Start quiz')}<Arrow className="w-4 h-4" /></Link>
    </div>
  );
};
